/**
 * NotFoundState
 *
 * Small presentational component for full-screen or inline "not found" UI.
 * Used by StartupSubPageShell, ScopeConversationPage, and ConversationModule
 * to render a consistent 404 / access-denied state.
 *
 * @module @ideoz/ui/components/molecules/NotFoundState
 */

import React from 'react';
import { Button } from '../atoms/button';
import { cn } from '../atoms/utils';

export interface NotFoundStateProps {
  /** Large heading text (default "404"). */
  title?: string;
  /** Description line under the heading (e.g. "Startup not found"). */
  description?: string;
  /** Optional action button label. */
  actionLabel?: string;
  /** Optional action button click handler. */
  onAction?: () => void;
  /** Extra className for the wrapper. */
  className?: string;
  /** testId for the root element. */
  testId?: string;
  /** When true, renders a compact inline variant (no min-h-screen) for use inside a pane. */
  inline?: boolean;
}

/**
 * NotFoundState Component
 *
 * Pure presentation — no business logic. Renders a centered 404 / not-found
 * state with an optional action button.
 *
 * @example
 * ```tsx
 * <NotFoundState description="Startup not found" actionLabel="Back to My Startups" onAction={() => navigate('/startups')} />
 * ```
 */
export const NotFoundState: React.FC<NotFoundStateProps> = ({
  title = '404',
  description = 'Not found',
  actionLabel,
  onAction,
  className = '',
  testId = 'not-found-state',
  inline = false,
}) => {
  return (
    <div
      data-testid={testId}
      className={cn(
        'flex flex-col items-center justify-center px-6 text-center',
        inline ? 'h-full' : 'min-h-screen',
        'bg-gray-50 dark:bg-gray-900',
        className,
      )}
    >
      <p className="text-6xl font-bold text-gray-300 dark:text-gray-600 mb-2">{title}</p>
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-1">
        {description}
      </h1>
      {actionLabel && onAction && (
        <Button onClick={onAction} className="mt-4">
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

export default NotFoundState;
